import React, { useEffect, useState } from 'react';
import { packsAPI } from '../../services/api';
import { formatDateTime, formatNumber } from './dataSourceFormat';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
// While waiting for the agent the page checks often, so the customer sees it connect.
const WAITING_POLL_MS = 10 * 1000;
const CONNECTED_POLL_MS = 60 * 1000;

const WHERE = {
  sage50: 'the computer where Sage 50 Accounts is installed. If Sage is used by several people, the computer or server that holds the Sage data is best.',
  sage200: 'a computer with the Sage 200 client installed, signed in as someone who uses Sage 200, such as the Sage server.',
};

function KeyField({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="pack-copy-field agent-key">
      <code aria-label="Agent key">{value}</code>
      <button type="button" className="btn-secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

function StatusLine({ status }) {
  const c = status.connection;
  if (c.state === 'waiting') {
    return <p className="agent-status agent-status--waiting" role="status"><span className="agent-dot" aria-hidden="true" />Waiting for the agent. This page updates when it connects.</p>;
  }
  const where = [c.productVersion, c.machine && `on ${c.machine}`, c.sourceCompany && `company ${c.sourceCompany}`].filter(Boolean).join(' ');
  if (c.state === 'offline') {
    return <p className="agent-status agent-status--offline" role="status"><span className="agent-dot" aria-hidden="true" />Not heard from since {formatDateTime(c.lastSeenAt)}{c.machine ? ` (${c.machine})` : ''}. Check that the computer is on and the Kinspeed Wallboard Agent service is running.</p>;
  }
  if (c.state === 'error') {
    return <p className="agent-status agent-status--error" role="status"><span className="agent-dot" aria-hidden="true" />{where ? `${where}: ` : ''}{c.error}</p>;
  }
  return <p className="agent-status agent-status--ok" role="status"><span className="agent-dot" aria-hidden="true" />Connected: {where}.{c.lastSyncAt ? ` Last sync ${formatDateTime(c.lastSyncAt)}.` : ' The first sync is running.'}</p>;
}

/**
 * Setting up and watching the Kinspeed Wallboard Agent, for a pack whose
 * system the wallboard cannot reach (Sage 50, Sage 200): download, the key
 * (shown once, or replaced) and the agent's last check-in.
 */
export default function AgentPanel({ pack, companyId, isAdmin, newKey = null }) {
  const [status, setStatus] = useState(pack.agentStatus);
  const [key, setKey] = useState(newKey?.apiKey || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (newKey?.apiKey) setKey(newKey.apiKey); }, [newKey]);
  useEffect(() => { setStatus(pack.agentStatus); }, [pack.agentStatus]);

  const state = status?.connection?.state;
  useEffect(() => {
    if (!state) return undefined;
    const every = state === 'connected' ? CONNECTED_POLL_MS : WAITING_POLL_MS;
    const timer = setInterval(async () => {
      try {
        const res = await packsAPI.get(pack.id, companyId);
        if (res?.success && res.data.agentStatus) setStatus(res.data.agentStatus);
      } catch { /* the next check tries again */ }
    }, every);
    return () => clearInterval(timer);
  }, [state, pack.id, companyId]);

  if (!status) return null;
  const connected = state === 'connected' || state === 'error' || state === 'offline';

  const replaceKey = async () => {
    if (connected && !window.confirm('The agent stops sending data until the new key is entered in it. Create a new key?')) return;
    try {
      setBusy(true);
      setError('');
      const res = await packsAPI.agentKey(pack.id, companyId);
      setKey(res.data.apiKey);
      setStatus((s) => ({ ...s, key: { ...(s.key || {}), prefix: res.data.keyPrefix, createdAt: res.data.createdAt } }));
    } catch (err) {
      setError(describeError(err, 'Could not create a new key'));
    } finally {
      setBusy(false);
    }
  };

  const steps = (
    <ol className="agent-steps">
      <li>
        <strong>Download the installer.</strong>
        {status.downloadUrl
          ? <p><a className="btn-primary" href={status.downloadUrl} download>Download the Kinspeed Wallboard Agent</a></p>
          : <p className="source-hint">The installer is not available to download yet. Contact Kinspeed for a copy.</p>}
      </li>
      <li>
        <strong>Copy your key.</strong>
        {key ? (
          <>
            <KeyField value={key} />
            <p className="source-hint">This key is shown once. Keep this page open until the installer has accepted it.</p>
          </>
        ) : (
          <p className="source-hint">
            {status.key ? <>The key was shown when {pack.title} was installed (it starts <code>{status.key.prefix}</code>). </> : null}
            If you no longer have it, create a new one.{connected ? ' The agent stops sending data until the new key is entered in it.' : ''}
          </p>
        )}
        {isAdmin && <p><button type="button" className="btn-secondary" onClick={replaceKey} disabled={busy}>{busy ? 'Creating…' : 'Create a new key'}</button></p>}
      </li>
      <li>
        <strong>Run the installer</strong> on {WHERE[status.product] || 'the computer where the software is installed.'} Paste the key when asked. The installer finds your companies, and you choose which one to send.
      </li>
    </ol>
  );

  return (
    <section className="source-card agent-panel" aria-labelledby="agent-heading">
      <h3 id="agent-heading">Kinspeed Wallboard Agent</h3>
      <p className="source-hint" style={{ marginTop: 0 }}>
        {pack.title} runs on your own computers, so the agent reads it there and sends it to the wallboard every hour. It only reads; it never changes anything in {pack.title}.
      </p>
      <StatusLine status={status} />
      {error && <div className="error-message" role="alert">{error}</div>}
      {connected && status.connection.datasets?.length > 0 && (
        <table className="datasets-table agent-datasets">
          <thead><tr><th>Dataset</th><th>Rows</th><th>Last sent</th><th /></tr></thead>
          <tbody>
            {status.connection.datasets.map((d) => (
              <tr key={d.name}>
                <td><code>{d.name}</code></td>
                <td>{d.rows === null ? '—' : formatNumber(d.rows)}</td>
                <td>{d.syncedAt ? formatDateTime(d.syncedAt) : '—'}</td>
                <td>{d.error ? <span className="agent-error">{d.error}</span> : d.skipped ? <span className="source-hint">{d.skipped}</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {connected && !key
        ? <details className="agent-setup"><summary>Set up the agent again, or on another computer</summary>{steps}</details>
        : steps}
    </section>
  );
}
